import { blueprintBg } from "@/theme/styleHelpers";
/**
 * CommandPalette — Cmd+K / Ctrl+K modal that's the application's
 * power-user entry point.
 *
 * Visual treatment is the brand's "industrial blueprint × IDE"
 * direction in miniature:
 *
 *   * Centered modal, 640px max-width, anchored 12vh from the top so
 *     it docks under the user's gaze instead of dead-centering on a
 *     tall viewport.
 *   * Mono input + mono command lines (JetBrains Mono) — every
 *     character is selectable signal.
 *   * Blueprint texture on the body, hairline borders, phosphor halo
 *     on the active row, single-stroke trace animation on open.
 *   * Sectioned by category with overline headers that read like
 *     drafting plate engravings.
 *
 * Keyboard contract:
 *   * Cmd+K / Ctrl+K   — toggle (handled by GlobalCommandPaletteHotkey)
 *   * ↑ / ↓             — move active row
 *   * Enter             — fire active command
 *   * Esc               — close
 *
 * Active-row scrolling: the active row's element scrolls into the
 * viewport with `block: nearest` so arrow nav past the visible window
 * doesn't trap the user.
 */
import KeyIcon from "@mui/icons-material/KeyboardCommandKeyRounded";
import SearchIcon from "@mui/icons-material/SearchRounded";
import { Backdrop, Box, Stack, Typography, alpha } from "@mui/material";
import { useCommandPalette } from "@stores/paletteStore";
import {
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { CATEGORY_LABEL, CATEGORY_ORDER, type Command } from "./commandTypes";
import { scoreCommand } from "./fuzzyScore";
import { useCommands } from "./useCommands";

export function CommandPalette() {
  const isOpen = useCommandPalette((s) => s.isOpen);
  const closePalette = useCommandPalette((s) => s.closePalette);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  const commands = useCommands({ onClose: closePalette });

  // Score + filter + group. The activeIdx is into the FLAT (already-
  // sorted) result list so arrow nav crosses category boundaries.
  const flat = useMemo<Command[]>(() => {
    const q = query.trim();
    const scored: Array<{ cmd: Command; score: number }> = [];
    for (const cmd of commands) {
      const score = scoreCommand(q, {
        label: cmd.label,
        hint: cmd.hint,
        keywords: cmd.keywords,
      });
      if (score > 0) scored.push({ cmd, score });
    }
    if (q) {
      // Sort by score desc when filtering — best matches first
      // regardless of category.
      scored.sort((a, b) => b.score - a.score);
      return scored.map((s) => s.cmd);
    }
    // No query — keep category order so the layout is predictable on
    // open. Within a category we preserve insertion order from
    // useCommands (which already sorts projects by recency).
    const grouped: Command[] = [];
    for (const cat of CATEGORY_ORDER) {
      for (const cmd of commands) {
        if (cmd.category === cat) grouped.push(cmd);
      }
    }
    return grouped;
  }, [query, commands]);

  // Reset state on open + focus input.
  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setActiveIdx(0);
    // Defer focus past the modal mount transition.
    const id = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  // Scroll active row into view on idx change.
  useEffect(() => {
    if (!isOpen) return;
    const el = listRef.current?.querySelector<HTMLDivElement>(
      `[data-cmd-idx="${activeIdx}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, isOpen]);

  // Clamp active idx whenever the result count shrinks.
  useEffect(() => {
    if (activeIdx >= flat.length && flat.length > 0) {
      setActiveIdx(0);
    }
  }, [flat.length, activeIdx]);

  if (!isOpen) return null;

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (flat.length === 0 ? 0 : (i + 1) % flat.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) =>
        flat.length === 0 ? 0 : (i - 1 + flat.length) % flat.length,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      flat[activeIdx]?.action();
    } else if (e.key === "Escape") {
      e.preventDefault();
      closePalette();
    }
  }

  // Walk the flat list once, marking category headers when the
  // category changes between adjacent items. Pre-computed so the
  // render below stays a flat map.
  const rows = buildRows(flat, query.length > 0);

  return (
    <Backdrop
      open={isOpen}
      onClick={closePalette}
      sx={{
        zIndex: (t) => t.zIndex.modal,
        backgroundColor: "rgba(7, 13, 24, 0.62)",
        backdropFilter: "blur(4px)",
        alignItems: "flex-start",
        pt: "12vh",
      }}
    >
      <Box
        role="dialog"
        aria-label="Command palette"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        sx={(t) => ({
          width: "min(640px, 92vw)",
          maxHeight: "min(72vh, 560px)",
          display: "flex",
          flexDirection: "column",
          bgcolor: t.palette.surface.default,
          border: `1px solid ${t.palette.border.default}`,
          borderRadius: 2,
          // Trace-stroke entrance — a single pulse of the cyan accent
          // around the dialog edge as it scales in. ~220ms; doesn't
          // loop. Falls back to a static dialog if the user has
          // prefers-reduced-motion set.
          animation: `cf-palette-in 220ms cubic-bezier(0.4, 0, 0.2, 1)`,
          boxShadow: `
            0 24px 48px -16px rgba(0, 0, 0, 0.55),
            0 0 0 1px ${alpha(t.palette.accent.main, 0.18)}
          `,
          "@keyframes cf-palette-in": {
            from: {
              opacity: 0,
              transform: "translateY(-6px) scale(0.98)",
              boxShadow: `0 0 0 0 ${alpha(t.palette.accent.main, 0)}`,
            },
            to: {
              opacity: 1,
              transform: "translateY(0) scale(1)",
            },
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        })}
      >
        {/* Search row */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={(t) => ({
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${t.palette.border.default}`,
          })}
        >
          <SearchIcon
            fontSize="small"
            sx={{ color: "primary.main", fontSize: 18 }}
          />
          <Box
            component="input"
            ref={inputRef as never}
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setQuery(e.target.value)
            }
            placeholder="Type to search · ↑↓ to navigate · ↵ to select"
            sx={(t) => ({
              flex: 1,
              minWidth: 0,
              fontFamily: t.fonts.mono,
              fontSize: 14,
              fontWeight: 500,
              background: "transparent",
              border: 0,
              outline: 0,
              color: t.palette.text.primary,
              letterSpacing: "0.01em",
              "&::placeholder": {
                color: t.palette.text.disabled,
                fontWeight: 400,
              },
            })}
          />
          <Box
            sx={(t) => ({
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              border: `1px solid ${t.palette.border.default}`,
              color: t.palette.text.disabled,
              fontFamily: t.fonts.mono,
              fontSize: 10,
              letterSpacing: "0.04em",
            })}
          >
            <KeyIcon sx={{ fontSize: 11 }} />K
          </Box>
        </Stack>

        {/* Results list */}
        <Box
          ref={listRef}
          sx={(t) => ({
            ...blueprintBg(t),
            flex: 1,
            overflowY: "auto",
            // The blueprint texture sits behind the rows; rows
            // themselves carry no background until active so the grid
            // shows through.
          })}
        >
          {rows.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center", color: "text.disabled" }}>
              <Typography variant="body2">
                No commands match "{query}"
              </Typography>
              <Typography
                variant="caption"
                sx={(t) => ({
                  display: "block",
                  mt: 0.5,
                  fontFamily: t.fonts.mono,
                  fontSize: 10,
                  letterSpacing: "0.04em",
                  color: "text.disabled",
                })}
              >
                Try fewer characters or another phrasing.
              </Typography>
            </Box>
          ) : (
            rows.map((row) => {
              if (row.kind === "header") {
                return (
                  <Box
                    key={`h-${row.label}`}
                    sx={(t) => ({
                      px: 2,
                      pt: 1.25,
                      pb: 0.5,
                      color: t.palette.text.disabled,
                    })}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: 10,
                        letterSpacing: "0.16em",
                      }}
                    >
                      {row.label}
                    </Typography>
                  </Box>
                );
              }
              const active = row.index === activeIdx;
              return (
                <Box
                  key={row.cmd.id}
                  data-cmd-idx={row.index}
                  onMouseEnter={() => setActiveIdx(row.index)}
                  onClick={row.cmd.action}
                  sx={(t) => ({
                    position: "relative",
                    cursor: "pointer",
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    bgcolor: active
                      ? alpha(t.palette.accent.main, 0.1)
                      : "transparent",
                    color: active
                      ? t.palette.text.primary
                      : t.palette.text.secondary,
                    "&::before": active
                      ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 2,
                          background: t.palette.accent.main,
                          boxShadow: t.palette.accent.glow,
                        }
                      : undefined,
                    transition: `background ${t.motion.fast}, color ${t.motion.fast}`,
                  })}
                >
                  <Box
                    sx={(t) => ({
                      display: "inline-flex",
                      width: 22,
                      height: 22,
                      borderRadius: 0.5,
                      bgcolor: active
                        ? t.palette.accent.softer
                        : t.palette.surface.raised,
                      color: active
                        ? t.palette.accent.main
                        : t.palette.text.disabled,
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    })}
                  >
                    {row.cmd.icon}
                  </Box>
                  <Box
                    sx={(t) => ({
                      flex: 1,
                      minWidth: 0,
                      fontFamily: t.fonts.mono,
                      fontSize: 13,
                      fontWeight: active ? 600 : 500,
                      letterSpacing: "0.01em",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    })}
                  >
                    {row.cmd.label}
                  </Box>
                  {row.cmd.hint && (
                    <Box
                      sx={(t) => ({
                        fontFamily: t.fonts.mono,
                        fontSize: 10,
                        color: t.palette.text.disabled,
                        whiteSpace: "nowrap",
                      })}
                    >
                      {row.cmd.hint}
                    </Box>
                  )}
                  {active && (
                    <Box
                      sx={(t) => ({
                        display: "inline-flex",
                        alignItems: "center",
                        px: 0.625,
                        py: 0.125,
                        borderRadius: 0.5,
                        border: `1px solid ${t.palette.border.default}`,
                        color: t.palette.text.disabled,
                        fontFamily: t.fonts.mono,
                        fontSize: 9,
                        letterSpacing: "0.04em",
                      })}
                    >
                      ↵
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer keyboard hint strip */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={(t) => ({
            px: 2,
            py: 0.75,
            borderTop: `1px solid ${t.palette.border.default}`,
            color: t.palette.text.disabled,
            fontFamily: t.fonts.mono,
            fontSize: 10,
            letterSpacing: "0.04em",
          })}
        >
          <FooterKey label="↑↓" caption="navigate" />
          <FooterKey label="↵" caption="select" />
          <FooterKey label="esc" caption="close" />
          <Box sx={{ flex: 1 }} />
          <Box>
            {flat.length} command{flat.length === 1 ? "" : "s"}
          </Box>
        </Stack>
      </Box>
    </Backdrop>
  );
}

// ────────────────────────────────────────────────────────────────────────────

type Row =
  | { kind: "header"; label: string }
  | { kind: "cmd"; cmd: Command; index: number };

function buildRows(flat: Command[], filtering: boolean): Row[] {
  const out: Row[] = [];
  let lastCategory: string | null = null;
  for (let i = 0; i < flat.length; i++) {
    const cmd = flat[i];
    if (!cmd) continue;
    // While filtering we don't render headers — the result list is
    // sorted by relevance, not category, so headers would lie.
    if (!filtering && cmd.category !== lastCategory) {
      out.push({ kind: "header", label: CATEGORY_LABEL[cmd.category] });
      lastCategory = cmd.category;
    }
    out.push({ kind: "cmd", cmd, index: i });
  }
  return out;
}

function FooterKey({ label, caption }: { label: string; caption: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box
        sx={(t) => ({
          display: "inline-flex",
          alignItems: "center",
          px: 0.5,
          py: 0,
          minWidth: 18,
          height: 16,
          justifyContent: "center",
          borderRadius: 0.5,
          border: `1px solid ${t.palette.border.default}`,
          color: t.palette.text.secondary,
          fontFamily: t.fonts.mono,
          fontSize: 10,
        })}
      >
        {label}
      </Box>
      <Box sx={{ fontSize: 10 }}>{caption}</Box>
    </Stack>
  );
}
